import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { addMonths, format, parse, startOfMonth } from 'date-fns'
import { FileDown, ShieldAlert } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { BalanceQuality, ReportSession, TutorBalanceRow, buildTutorBalanceReport, fetchStudentBalancesAt, reportCutoff } from '../lib/balance'

const PAGE = 1000

async function fetchMonthSessions(from: Date, to: Date): Promise<ReportSession[]> {
  const out: ReportSession[] = []
  for (let page = 0; ; page++) {
    const { data, error } = await supabase
      .from('sessions')
      .select('id, tutor_id, student_id, minutes, approval_status, lifecycle_status, is_free, over_balance')
      .gte('started_at', from.toISOString())
      .lt('started_at', to.toISOString())
      .order('started_at')
      .order('id')
      .range(page * PAGE, page * PAGE + PAGE - 1)
    if (error) throw error
    for (const r of data ?? []) {
      out.push({
        tutorId: r.tutor_id,
        studentId: r.student_id,
        minutes: r.minutes ?? 0,
        approvalStatus: r.approval_status,
        lifecycleStatus: r.lifecycle_status,
        isFree: !!r.is_free,
        overBalance: !!r.over_balance,
      })
    }
    if (!data || data.length < PAGE) break
  }
  return out
}

export function TutorBalanceReport() {
  const [month, setMonth] = useState(format(new Date(), 'yyyy-MM'))
  const [rows, setRows] = useState<TutorBalanceRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [asOf, setAsOf] = useState<Date | null>(null)
  const [qualityCounts, setQualityCounts] = useState<Record<BalanceQuality, number>>({ exact: 0, estimated: 0, current: 0 })

  useEffect(() => {
    setRows(null)
    setError(null)
    ;(async () => {
      try {
        const from = startOfMonth(parse(month, 'yyyy-MM', new Date()))
        const to = addMonths(from, 1)
        const cutoff = reportCutoff(from)
        const [sessions, { balances, quality }, tutors] = await Promise.all([
          fetchMonthSessions(from, to),
          fetchStudentBalancesAt(cutoff),
          supabase.from('tutors').select('id, name'),
        ])
        const report = buildTutorBalanceReport(sessions, balances, (tutors.data ?? []) as { id: string; name: string }[])
        const shown = new Set<string>()
        sessions.forEach(s => s.studentId && shown.add(s.studentId))
        const counts: Record<BalanceQuality, number> = { exact: 0, estimated: 0, current: 0 }
        shown.forEach(id => { const q = quality[id]; if (q) counts[q] += 1 })
        setQualityCounts(counts)
        setAsOf(cutoff)
        setRows(report)
      } catch (e: any) {
        setError(e?.message || 'Could not build the report.')
      }
    })()
  }, [month])

  const exportCSV = () => {
    if (!rows) return
    const header = ['Tutor', 'Sessions', 'Minutes', 'Approved', 'Pending', 'Over balance', `Students at zero on ${asOf ? format(asOf, 'yyyy-MM-dd') : ''}`, 'Minutes owed', 'Students']
    const lines = rows.map(r => [
      r.tutorName, r.sessions, r.minutes, r.approved, r.pending, r.overBalance,
      r.studentsAtZero.length, r.minutesOwed,
      r.studentsAtZero.map(s => `${s.name} (${s.balanceMinutes})`).join('; '),
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
    const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `sessions_vs_balance_${month}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const th = 'px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'
  const td = 'px-4 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap'

  return (
    <div className="bg-white dark:bg-surface-100 rounded-lg shadow p-6 mb-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Sessions logged vs balance, per tutor</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Sessions started in the month (cancelled and free sessions left out). Balances are as of{' '}
            {asOf ? format(asOf, 'MMM d, yyyy h:mm a') : '…'}
            {asOf && asOf.getTime() > Date.now() - 60_000 ? " (now: this month isn't over)" : ' (end of the month)'}.
          </p>
          {(qualityCounts.estimated > 0 || qualityCounts.current > 0) && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
              {qualityCounts.estimated > 0 && `${qualityCounts.estimated} student balance(s) estimated from the minutes ledger (before exact tracking started). `}
              {qualityCounts.current > 0 && `${qualityCounts.current} student(s) have no balance history, so today's balance is used.`}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="month"
            value={month}
            onChange={(e) => e.target.value && setMonth(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-surface-0 text-gray-900 dark:text-white text-sm"
          />
          <button
            onClick={exportCSV}
            disabled={!rows || rows.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-0 disabled:opacity-40"
          >
            <FileDown className="h-4 w-4" /> CSV
          </button>
        </div>
      </div>

      {error ? (
        <p className="text-sm text-red-500">{error}</p>
      ) : !rows ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-400" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">No sessions logged in this month.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className={th}>Tutor</th>
                <th className={th}>Sessions</th>
                <th className={th}>Minutes</th>
                <th className={th}>Approved</th>
                <th className={th}>Pending</th>
                <th className={th}>Over balance</th>
                <th className={th}>Students at zero (as of date)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {rows.map(r => (
                <tr key={r.tutorId} className="align-top">
                  <td className={td + ' font-medium'}>{r.tutorName}</td>
                  <td className={td}>{r.sessions}</td>
                  <td className={td}>{r.minutes}</td>
                  <td className={td}>{r.approved}</td>
                  <td className={td}>{r.pending}</td>
                  <td className={td}>
                    {r.overBalance > 0 ? (
                      <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 font-medium">
                        <ShieldAlert className="h-3.5 w-3.5" /> {r.overBalance}
                      </span>
                    ) : (
                      0
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {r.studentsAtZero.length === 0 ? (
                      <span className="text-gray-400">None</span>
                    ) : (
                      <div className="space-y-0.5">
                        <p className="text-red-600 dark:text-red-400 font-medium">
                          {r.studentsAtZero.length} · {r.minutesOwed} min owed
                        </p>
                        {r.studentsAtZero.map(s => (
                          <Link key={s.id} to={`/dashboard/students/${s.id}`} className="block text-xs text-gray-600 dark:text-gray-300 hover:underline">
                            {s.name} ({s.balanceMinutes} min)
                          </Link>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
